import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination from '../components/Pagination';
import { getAdminToken } from '../utils/adminAuth';
import { createNumberFormatter, formatDateTime, normalizeUiLocale } from '../utils/locale';

const emptyRegisterForm = {
  username: '',
  password: '',
  name: '',
  title: '',
  specialty: '',
  license_number: ''
};

const emptyEditForm = {
  username: '',
  name: '',
  title: '',
  specialty: '',
  license_number: ''
};

const DoctorList = () => {
  const [doctors, setDoctors] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    doctorId: null,
    doctorName: ''
  });
  const [editingDoctor, setEditingDoctor] = useState({
    show: false,
    doctorId: null,
    form: emptyEditForm
  });

  const { t, i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const numberFormatter = createNumberFormatter(locale);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const fetchDoctors = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      const response = await fetch(`/api/v1/doctors?limit=${pageSize}&offset=${offset}`, {
        headers: {
          Authorization: `Bearer ${getAdminToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctors');
      }

      const data = await response.json();
      const nextDoctors = Array.isArray(data) ? data : [];
      const totalHeader = Number.parseInt(response.headers.get('X-Total-Count'), 10);
      const nextTotal = Number.isFinite(totalHeader) ? totalHeader : nextDoctors.length;
      const lastPage = Math.max(1, Math.ceil(nextTotal / pageSize));

      setTotalCount(nextTotal);
      if (currentPage > lastPage) {
        setCurrentPage(lastPage);
        return;
      }

      setDoctors(nextDoctors);
      setError('');
    } catch (requestError) {
      console.error('Error fetching doctors:', requestError);
      setError(t('doctors.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, t]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const handleRegisterChange = (field, value) => {
    setRegisterForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();

    if (!registerForm.username || !registerForm.password || !registerForm.name) {
      setError(t('doctors.requiredFields'));
      return;
    }

    try {
      const response = await fetch('/api/v1/doctors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify(registerForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }

      setRegisterForm(emptyRegisterForm);
      setShowRegister(false);
      setError('');
      const lastPage = Math.max(1, Math.ceil((totalCount + 1) / pageSize));
      if (lastPage === currentPage) {
        fetchDoctors();
      } else {
        setCurrentPage(lastPage);
      }
    } catch (requestError) {
      console.error('Registration error:', requestError);
      setError(`${t('doctors.registerError')}: ${requestError.message}`);
    }
  };

  const handleEdit = (doctorId) => {
    const doctor = doctors.find((item) => item.id === doctorId);
    if (!doctor) {
      return;
    }

    setEditingDoctor({
      show: true,
      doctorId,
      form: {
        username: doctor.username || '',
        name: doctor.name || '',
        title: doctor.title || '',
        specialty: doctor.specialty || '',
        license_number: doctor.license_number || ''
      }
    });
  };

  const handleEditChange = (field, value) => {
    setEditingDoctor((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [field]: value
      }
    }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();

    try {
      const response = await fetch(`/api/v1/doctors/${editingDoctor.doctorId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify(editingDoctor.form)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Update failed');
      }

      setEditingDoctor({ show: false, doctorId: null, form: emptyEditForm });
      setError('');
      fetchDoctors();
    } catch (requestError) {
      console.error('Edit error:', requestError);
      setError(`${t('doctors.editError')}: ${requestError.message}`);
    }
  };

  const handleDelete = (doctorId) => {
    const doctor = doctors.find((item) => item.id === doctorId);
    setDeleteConfirm({
      show: true,
      doctorId,
      doctorName: doctor?.name || 'Unknown'
    });
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`/api/v1/doctors/${deleteConfirm.doctorId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getAdminToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete doctor');
      }

      setDeleteConfirm({ show: false, doctorId: null, doctorName: '' });
      setError('');
      const nextTotal = Math.max(0, totalCount - 1);
      const lastPage = Math.max(1, Math.ceil(nextTotal / pageSize));
      if (currentPage > lastPage) {
        setCurrentPage(lastPage);
      } else {
        fetchDoctors();
      }
    } catch (requestError) {
      console.error('Error deleting doctor:', requestError);
      setError(t('doctors.deleteError'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return t('dashboard.notSet');
    }

    return formatDateTime(dateString, locale) || t('dashboard.notSet');
  };

  const closeRegisterDialog = () => {
    setShowRegister(false);
    setRegisterForm(emptyRegisterForm);
  };

  const closeEditDialog = () => {
    setEditingDoctor({ show: false, doctorId: null, form: emptyEditForm });
  };

  return (
    <div className="admin-page">
      <div className="page-header admin-header-panel">
        <div className="page-title-block">
          <h1 className="page-title">{t('dashboard.doctorManagement')}</h1>
        </div>

        <div className="page-actions">
          <button
            type="button"
            className="theme-button theme-button--secondary"
            onClick={showRegister ? closeRegisterDialog : () => setShowRegister(true)}
          >
            {showRegister ? t('doctors.cancelRegister') : t('doctors.register')}
          </button>
          <button
            type="button"
            className="theme-button theme-button--ghost"
            onClick={fetchDoctors}
          >
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      {showRegister && (
        <div className="dialog-backdrop">
          <div
            className="dialog-shell dialog-shell--medium admin-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="doctor-register-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="page-title-block">
              <h2 id="doctor-register-title" className="dialog-shell__title">{t('doctors.registerNew')}</h2>
            </div>

            {error && (
              <div className="status-banner status-banner--error">
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleRegisterSubmit} className="stack">
              <div className="form-grid">
                <div className="field">
                  <label className="field__label" htmlFor="register-username">{t('auth.username')}</label>
                  <input
                    id="register-username"
                    className="theme-input"
                    type="text"
                    value={registerForm.username}
                    onChange={(event) => handleRegisterChange('username', event.target.value)}
                    required
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="register-password">{t('auth.password')}</label>
                  <input
                    id="register-password"
                    className="theme-input"
                    type="password"
                    value={registerForm.password}
                    onChange={(event) => handleRegisterChange('password', event.target.value)}
                    required
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="register-name">{t('auth.name')}</label>
                  <input
                    id="register-name"
                    className="theme-input"
                    type="text"
                    value={registerForm.name}
                    onChange={(event) => handleRegisterChange('name', event.target.value)}
                    required
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="register-title">{t('doctors.title')}</label>
                  <input
                    id="register-title"
                    className="theme-input"
                    type="text"
                    value={registerForm.title}
                    onChange={(event) => handleRegisterChange('title', event.target.value)}
                    placeholder={t('doctors.titlePlaceholder')}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="register-specialty">{t('doctors.specialty')}</label>
                  <input
                    id="register-specialty"
                    className="theme-input"
                    type="text"
                    value={registerForm.specialty}
                    onChange={(event) => handleRegisterChange('specialty', event.target.value)}
                    placeholder={t('doctors.specialtyPlaceholder')}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="register-license">{t('doctors.licenseNumber')}</label>
                  <input
                    id="register-license"
                    className="theme-input"
                    type="text"
                    value={registerForm.license_number}
                    onChange={(event) => handleRegisterChange('license_number', event.target.value)}
                    placeholder={t('doctors.licenseNumberPlaceholder')}
                  />
                </div>
              </div>

              <div className="dialog-shell__actions">
                <button type="submit" className="theme-button theme-button--success">
                  {t('doctors.register')}
                </button>
                <button
                  type="button"
                  className="theme-button theme-button--ghost"
                  onClick={closeRegisterDialog}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingDoctor.show && (
        <div className="dialog-backdrop">
          <div
            className="dialog-shell dialog-shell--medium admin-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="doctor-edit-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="page-title-block">
              <h2 id="doctor-edit-title" className="dialog-shell__title">{t('doctors.editDoctor')}</h2>
            </div>

            {error && (
              <div className="status-banner status-banner--error">
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleEditSubmit} className="stack">
              <div className="form-grid">
                <div className="field">
                  <label className="field__label" htmlFor="edit-username">{t('auth.username')}</label>
                  <input
                    id="edit-username"
                    className="theme-input"
                    type="text"
                    value={editingDoctor.form.username}
                    onChange={(event) => handleEditChange('username', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="edit-name">{t('auth.name')}</label>
                  <input
                    id="edit-name"
                    className="theme-input"
                    type="text"
                    value={editingDoctor.form.name}
                    onChange={(event) => handleEditChange('name', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="edit-title">{t('doctors.title')}</label>
                  <input
                    id="edit-title"
                    className="theme-input"
                    type="text"
                    value={editingDoctor.form.title}
                    onChange={(event) => handleEditChange('title', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="edit-specialty">{t('doctors.specialty')}</label>
                  <input
                    id="edit-specialty"
                    className="theme-input"
                    type="text"
                    value={editingDoctor.form.specialty}
                    onChange={(event) => handleEditChange('specialty', event.target.value)}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="edit-license">{t('doctors.licenseNumber')}</label>
                  <input
                    id="edit-license"
                    className="theme-input"
                    type="text"
                    value={editingDoctor.form.license_number}
                    onChange={(event) => handleEditChange('license_number', event.target.value)}
                  />
                </div>
              </div>

              <div className="dialog-shell__actions">
                <button type="submit" className="theme-button theme-button--primary">
                  {t('doctors.update')}
                </button>
                <button
                  type="button"
                  className="theme-button theme-button--ghost"
                  onClick={closeEditDialog}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <section className="surface-panel admin-panel">
        <div className="surface-panel__body stack">
          <div className="admin-section-header">
            <div className="admin-stat-strip">
              <span className="status-pill status-pill--success">
                {t('pagination.total')}: {numberFormatter.format(totalCount)}
              </span>
              <span className="status-pill status-pill--neutral">
                {t('pagination.page')} {numberFormatter.format(currentPage)} / {numberFormatter.format(totalPages)}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-orb" />
              <span>{t('common.loading')}</span>
            </div>
          ) : doctors.length === 0 ? (
            <div className="empty-state">
              <h3 className="auth-form__title">{t('doctors.noDoctors')}</h3>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{t('auth.username')}</th>
                    <th>{t('auth.name')}</th>
                    <th>{t('doctors.title')}</th>
                    <th>{t('doctors.specialty')}</th>
                    <th>{t('doctors.licenseNumber')}</th>
                    <th>{t('doctors.createdAt')}</th>
                    <th>{t('doctors.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {doctors.map((doctor) => (
                    <tr key={doctor.id}>
                      <td>
                        <div className="table-title">#{doctor.id}</div>
                      </td>
                      <td>{doctor.username}</td>
                      <td>{doctor.name}</td>
                      <td>{doctor.title || t('dashboard.notSet')}</td>
                      <td>{doctor.specialty || t('dashboard.notSet')}</td>
                      <td>{doctor.license_number || t('dashboard.notSet')}</td>
                      <td>{formatDate(doctor.created_at)}</td>
                      <td>
                        <div className="doctor-table__actions">
                          <button
                            type="button"
                            className="theme-button theme-button--ghost theme-button--compact"
                            onClick={() => handleEdit(doctor.id)}
                          >
                            {t('doctors.edit')}
                          </button>
                          <button
                            type="button"
                            className="theme-button theme-button--danger theme-button--compact"
                            onClick={() => handleDelete(doctor.id)}
                          >
                            {t('doctors.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </div>
      </section>

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, doctorId: null, doctorName: '' })}
        onConfirm={confirmDelete}
        title={t('doctors.confirmDelete')}
        message={t('doctors.confirmDeleteMessage', { name: deleteConfirm.doctorName })}
        confirmText={t('doctors.delete')}
        cancelText={t('common.cancel')}
        type="danger"
      />
    </div>
  );
};

export default DoctorList;
